import React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { Trans, useTranslation } from 'react-i18next';
import { Namespace } from 'i18next';
import { Button, Error } from '@unmind/design-system';
import { small } from '../../utils';
import {
  LoggedOutScreenTitle,
  LoggedOutScreenSubtitle,
  createTextContentWidth,
  LoggedOutScreenWrapper,
} from '../LoggedOutScreenWrapper';
import LoadingIndicator from '../../Shared/LoadingIndicator';
import useSSO, { SSOOrigin } from '../UniversalLogin/useSSO';
import getSubdomainFromUrl from '../../utils/getSubdomainFromUrl';
import RoutePath from '../../App/RoutePath';
import { tracking } from '../../App/Tracking';
import { ErrorTranslationKey, getAuthErrorMessage } from './AuthError';

export type LoginViaSSOProps = RouteComponentProps;

const ContinueButton = styled(Button)`
  margin-top: ${rem(24)};

  ${small(css`
    margin-top: ${rem(40)};
  `)}
`;

const BoldLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  font-size: inherit;
`;

const ButtonTextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlusOneTextWrapper = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  ${createTextContentWidth()};
  font-size: ${rem('12px')};
  line-height: 140%;
  font-weight: 400;
  margin-top: ${rem(24)};
`;

function LoginViaSSO({}: LoginViaSSOProps) {
  const subdomain = getSubdomainFromUrl();

  const { t: translate } =
    useTranslation<Namespace<'logged_out'>>('logged_out');

  const urlSearchParams = new URLSearchParams(location.search);
  const { errorCode, errorMessage } = getAuthErrorMessage(urlSearchParams);

  const { loading, ssoProviderName, startSSO } = useSSO();

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <LoggedOutScreenWrapper>
        <LoggedOutScreenTitle>
          {ssoProviderName
            ? translate('sso.access_unmind_header.sso_provider', {
                sso_provider: ssoProviderName,
              })
            : translate('sso.access_unmind_header.default')}
        </LoggedOutScreenTitle>
        <LoggedOutScreenSubtitle>
          {translate('sso.access_unmind_subtitle')}
        </LoggedOutScreenSubtitle>
        <ContinueButton
          variant="primary"
          data-testid="sso-sign-in-button"
          label={
            <ButtonTextWrapper>
              {ssoProviderName
                ? translate('sso.continue_button.sso_provider_label', {
                    sso_provider: ssoProviderName,
                  })
                : translate('sso.continue_button.default_label')}
            </ButtonTextWrapper>
          }
          ariaLabel={translate('sso.continue_button.aria_label')}
          /* Unknown, not Login: this screen is where sign-ups for SSO-only orgs
           * are sent too, so it cannot tell the two journeys apart. */
          onClick={() => void startSSO(SSOOrigin.Unknown)}
        />
        <PlusOneTextWrapper data-testid="sso-plus-one-text">
          <Trans
            t={translate}
            i18nKey="sso.plus_one_footer_help_text"
            defaults="Been given guest access to {{brandName}} by a friend or family member? <colour>Sign in with your email address and password</colour>."
            components={{
              colour: (
                <BoldLink
                  to={{ pathname: RoutePath.LoginWithCredentials }}
                  onClick={() => {
                    tracking.track('sso-plus-one-signin-link-clicked', {
                      subdomain,
                    });
                  }}
                />
              ),
            }}
          />
        </PlusOneTextWrapper>
        {errorCode && (
          <Error
            errorText={translate(errorMessage as ErrorTranslationKey)}
            showIcon
          />
        )}
      </LoggedOutScreenWrapper>
    </>
  );
}

export default LoginViaSSO;
